import type { Day, Locale } from "date-fns";
import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  format,
  getDate,
  getWeek,
  isToday,
  isWeekend,
  startOfMonth,
  startOfWeek,
} from "date-fns";

import type { DayCell, WeekRow } from "./datetimePickerTypes";

/** ISO-style options so week numbers match the European calendar (week starts Monday, first week contains Jan 4th). */
function weekOptions(weekStartsOn: Day) {
  return { weekStartsOn, firstWeekContainsDate: 4 as const };
}

/**
 * Build the visible week rows for a month. Renders only as many weeks as needed to cover the
 * month (4–6 rows), padded with the trailing/leading days from adjacent months.
 */
export function buildMonthRows(month: Date, weekStartsOn: Day, isDisabled?: (date: Date) => boolean): WeekRow[] {
  const firstOfMonth = startOfMonth(month);
  const gridStart = startOfWeek(firstOfMonth, { weekStartsOn });
  const leadingDays = differenceInCalendarDays(firstOfMonth, gridStart);
  const daysInMonth = getDate(endOfMonth(month));
  const weeks = Math.ceil((leadingDays + daysInMonth) / 7);
  const monthIndex = firstOfMonth.getMonth();

  const rows: WeekRow[] = [];
  let cursor = gridStart;
  for (let w = 0; w < weeks; w++) {
    const days: DayCell[] = [];
    for (let d = 0; d < 7; d++) {
      const date = addDays(cursor, d);
      days.push({
        date,
        day: date.getDate(),
        inCurrentMonth: date.getMonth() === monthIndex,
        isToday: isToday(date),
        isWeekend: isWeekend(date),
        disabled: isDisabled ? isDisabled(date) : false,
      });
    }
    // Use the row's Thursday so the ISO week number is correct regardless of weekStartsOn.
    const thursday = addDays(cursor, (4 - weekStartsOn + 7) % 7);
    rows.push({ weekNumber: getWeek(thursday, weekOptions(weekStartsOn)), days });
    cursor = addDays(cursor, 7);
  }
  return rows;
}

/** Short weekday labels (e.g. `Mo Tu We …`) ordered by `weekStartsOn`, localized. */
export function weekdayLabels(weekStartsOn: Day, locale: Locale): string[] {
  const base = startOfWeek(new Date(2024, 0, 1), { weekStartsOn }); // a known Monday
  return Array.from({ length: 7 }, (_, i) => format(addDays(base, i), "EEEEEE", { locale }));
}

export { isToday, isWeekend };
